// Contrail V3.13.0 — Admin → Integrations (Super Admins): keys for the read-only partner track feed (/api/feed/track).
// A new key is shown ONCE here (only its hash is stored). Paste it straight into the partner's server settings.
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { adminApi, type Org } from "../../lib/admin";
import { Drawer, Field, Notice, Toolbar, fmtDate, fmtDateTime } from "./ui";

type FeedKey = {
  id: string; name: string; key_hint: string; org_ids: string[]; device_ids: string[];
  rate_per_min: number; max_hours: number; created_at: string; revoked_at: string | null; last_used_at: string | null;
};
type LogRow = { id: number; key_id: string; device_id: string | null; from_at: string | null; to_at: string | null; points: number | null; status: number; ms: number | null; at: string };
type Dev = { id: string; name: string; org_id: string | null; is_simulated: boolean };

const STATUS_TEXT: Record<number, [string, string]> = {
  200: ["ok", "OK"], 400: ["warn", "Bad request"], 403: ["bad", "Not allowed"], 404: ["warn", "Unknown device"],
  429: ["warn", "Rate limited"], 502: ["bad", "Server error"],
};

function CopyBtn({ text, label = "Copy" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return <button type="button" className="btn-ghost small" onClick={() => { void navigator.clipboard?.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}>{done ? "Copied" : label}</button>;
}

export default function Integrations({ orgs }: { orgs: Org[] }) {
  const [keys, setKeys] = useState<FeedKey[] | null>(null);
  const [log, setLog] = useState<LogRow[]>([]);
  const [devs, setDevs] = useState<Dev[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "bad"; text: string } | null>(null);
  const [secret, setSecret] = useState<{ name: string; value: string } | null>(null);
  const [edit, setEdit] = useState<FeedKey | "new" | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  const feedUrl = `${window.location.origin}/api/feed/track`;

  const load = useCallback(async () => {
    try {
      const r = await adminApi<{ keys: FeedKey[]; log: LogRow[] }>("GET", "feed-keys");
      setKeys(r.keys); setLog(r.log); setErr(null);
    } catch (e: any) { setErr(e.message); }
  }, []);
  useEffect(() => {
    void load();
    supabase.from("devices").select("id, name, org_id, is_simulated").order("name").then(({ data }) => setDevs((data ?? []) as Dev[]));
  }, [load]);

  const orgName = (id: string | null) => orgs.find((o) => o.id === id)?.name ?? "—";
  const devName = (id: string | null) => devs.find((d) => d.id === id)?.name ?? (id ? id.slice(0, 8) + "…" : "—");
  const keyName = (id: string) => keys?.find((k) => k.id === id)?.name ?? "—";
  const covered = (k: FeedKey) => devs.filter((d) => !d.is_simulated && (k.device_ids.includes(d.id) || (!!d.org_id && k.org_ids.includes(d.org_id))));
  const active = (keys ?? []).filter((k) => !k.revoked_at);
  const coveredAll = useMemo(() => {
    const m = new Map<string, Dev>();
    for (const k of active) for (const d of covered(k)) m.set(d.id, d);
    return [...m.values()].sort((a, b) => orgName(a.org_id).localeCompare(orgName(b.org_id)) || a.name.localeCompare(b.name));
  }, [keys, devs, orgs]); // eslint-disable-line react-hooks/exhaustive-deps

  async function revoke(k: FeedKey) {
    setMsg(null);
    try {
      await adminApi("POST", `feed-keys/${k.id}/revoke`, {});
      setMsg({ kind: "ok", text: `"${k.name}" revoked. Calls with it are refused from now on.` });
      setConfirmRevoke(null); await load();
    } catch (e: any) { setMsg({ kind: "bad", text: e.message }); }
  }

  return (
    <section className="integrations">
      <Toolbar>
        <h1>Integrations</h1>
        <span className="sub">Read-only track feed for partner systems, server to server.</span>
        <button className="btn-primary" onClick={() => { setSecret(null); setEdit("new"); }}>+ New feed key</button>
      </Toolbar>
      {err && <Notice kind="bad">{err}</Notice>}
      {msg && <Notice kind={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Notice>}
      {secret && (
        <Notice kind="warn" onClose={() => setSecret(null)}>
          <strong>New key for "{secret.name}"</strong>
          <div className="temp-pw">
            <code>{secret.value}</code>
            <CopyBtn text={secret.value} />
            <div className="sub">Shown once only — Contrail keeps just a fingerprint of it. Paste it straight into the partner's server settings
              (e.g. a Netlify environment variable). Don't send it by chat or email. Lost it? Revoke this key and make a new one.</div>
          </div>
        </Notice>
      )}

      <div className="int-card">
        <h2>Feed address</h2>
        <div className="int-url"><code>GET {feedUrl}?device=&lt;Contrail device ID&gt;&amp;from=&lt;ISO UTC&gt;&amp;to=&lt;ISO UTC&gt;</code><CopyBtn text={feedUrl} label="Copy URL" /></div>
        <p className="sub">Header <code>x-feed-key: &lt;key&gt;</code>. Up to 48 hours per call, one point every 15 s at most, cleaned the same way as History.
          Answers 400 bad dates · 401 bad key · 403 device not allowed · 404 unknown device · 429 too many calls · 502 tracking server down.</p>
      </div>

      <h2 className="int-h">Keys</h2>
      <div className="atable-wrap">
        <table className="atable static">
          <thead><tr><th>Name</th><th>Can read</th><th>Limit</th><th>Last used</th><th>Created</th><th>Status</th><th /></tr></thead>
          <tbody>
            {keys === null && !err && <tr className="empty"><td colSpan={7}>Loading…</td></tr>}
            {keys?.length === 0 && <tr className="empty"><td colSpan={7}>No feed keys yet.</td></tr>}
            {keys?.map((k) => (
              <tr key={k.id}>
                <td><strong>{k.name}</strong><div className="sub">…{k.key_hint}</div></td>
                <td>
                  {k.org_ids.map((id) => <div key={id}>{orgName(id)} <span className="sub">(all trackers)</span></div>)}
                  {k.device_ids.map((id) => <div key={id}>{devName(id)}</div>)}
                  <div className="sub">{covered(k).length} tracker{covered(k).length === 1 ? "" : "s"} now</div>
                </td>
                <td className="sub">{k.rate_per_min}/min · {k.max_hours} h</td>
                <td>{fmtDateTime(k.last_used_at)}</td>
                <td>{fmtDate(k.created_at)}</td>
                <td>{k.revoked_at ? <span className="apill bad" title={fmtDateTime(k.revoked_at)}>Revoked</span> : <span className="apill ok">Active</span>}</td>
                <td className="int-act">
                  {!k.revoked_at && confirmRevoke !== k.id && <>
                    <button className="btn-ghost small" onClick={() => setEdit(k)}>Edit</button>
                    <button className="btn-ghost small" onClick={() => setConfirmRevoke(k.id)}>Revoke</button>
                  </>}
                  {confirmRevoke === k.id && <>
                    <button className="btn-ghost small danger" onClick={() => revoke(k)}>Yes, revoke</button>
                    <button className="btn-ghost small" onClick={() => setConfirmRevoke(null)}>Cancel</button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="int-h">Device IDs for partners</h2>
      <p className="sub">Trackers covered by an active key. Give the partner the Contrail ID — it stays the same if the tracker hardware is swapped.</p>
      <div className="atable-wrap">
        <table className="atable static">
          <thead><tr><th>Tracker</th><th>Company</th><th>Contrail device ID</th></tr></thead>
          <tbody>
            {coveredAll.length === 0 && <tr className="empty"><td colSpan={3}>No trackers covered yet.</td></tr>}
            {coveredAll.map((d) => (
              <tr key={d.id}><td>{d.name}</td><td>{orgName(d.org_id)}</td><td className="int-id"><code>{d.id}</code><CopyBtn text={d.id} /></td></tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="int-h">Call log <span className="sub">latest 200 · positions are never logged</span></h2>
      <div className="atable-wrap">
        <table className="atable static">
          <thead><tr><th>When</th><th>Key</th><th>Tracker</th><th>Window (Fiji time)</th><th className="num">Points</th><th>Result</th></tr></thead>
          <tbody>
            {log.length === 0 && <tr className="empty"><td colSpan={6}>No calls yet.</td></tr>}
            {log.map((r) => {
              const [tone, text] = STATUS_TEXT[r.status] ?? ["plain", String(r.status)];
              return (
                <tr key={r.id}>
                  <td>{fmtDateTime(r.at)}</td><td>{keyName(r.key_id)}</td><td>{devName(r.device_id)}</td>
                  <td className="sub">{r.from_at ? `${fmtDateTime(r.from_at)} → ${fmtDateTime(r.to_at)}` : "—"}</td>
                  <td className="num">{r.points ?? "—"}</td>
                  <td><span className={`apill ${tone}`}>{text}</span>{r.ms != null && <span className="sub"> {r.ms} ms</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {edit && <KeyDrawer k={edit === "new" ? null : edit} orgs={orgs} devs={devs} onClose={() => setEdit(null)}
        onSaved={(r) => {
          setEdit(null);
          if (r.secret) setSecret({ name: r.name, value: r.secret });
          else setMsg({ kind: "ok", text: `"${r.name}" saved.` });
          void load();
        }} />}
    </section>
  );
}

function KeyDrawer({ k, orgs, devs, onClose, onSaved }: {
  k: FeedKey | null; orgs: Org[]; devs: Dev[]; onClose: () => void; onSaved: (r: { name: string; secret?: string }) => void;
}) {
  const [name, setName] = useState(k?.name ?? "");
  const [orgIds, setOrgIds] = useState<string[]>(k?.org_ids ?? []);
  const [devIds, setDevIds] = useState<string[]>(k?.device_ids ?? []);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const tog = (list: string[], set: (v: string[]) => void, id: string) => set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const orgName = (id: string | null) => orgs.find((o) => o.id === id)?.name ?? "";
  const shownDevs = devs.filter((d) => !d.is_simulated && (!q.trim() || `${d.name} ${orgName(d.org_id)}`.toLowerCase().includes(q.trim().toLowerCase())));
  const ok = name.trim() && (orgIds.length || devIds.length);

  async function save() {
    setBusy(true); setErr(null);
    try {
      const body = { name: name.trim(), org_ids: orgIds, device_ids: devIds };
      if (k) { await adminApi("PATCH", `feed-keys/${k.id}`, body); onSaved({ name: body.name }); }
      else { const r = await adminApi<{ secret: string }>("POST", "feed-keys", body); onSaved({ name: body.name, secret: r.secret }); }
    } catch (e: any) { setErr(e.message); setBusy(false); }
  }

  return (
    <Drawer title={k ? `Edit "${k.name}"` : "New feed key"} onClose={onClose}
      footer={<>
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!ok || busy} onClick={save}>{busy ? "Saving…" : k ? "Save" : "Create key"}</button>
      </>}>
      {err && <Notice kind="bad">{err}</Notice>}
      <Field label="Name" hint="Who uses it, e.g. MMPRI C3"><input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} autoFocus /></Field>
      {/* not <Field>: it is a <label>, and these lists hold their own checkbox labels */}
      <div className="afield wide" role="group" aria-label="Companies">
        <span>Companies</span>
        <div className="int-pick">
          {orgs.map((o) => <label key={o.id} className="acheck"><input type="checkbox" checked={orgIds.includes(o.id)} onChange={() => tog(orgIds, setOrgIds, o.id)} />{o.name}</label>)}
        </div>
        <small>Every tracker in these companies, including ones added later.</small>
      </div>
      <div className="afield wide" role="group" aria-label="Single trackers">
        <span>Single trackers</span>
        <input className="asearch" placeholder="Search trackers" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="int-pick">
          {shownDevs.map((d) => <label key={d.id} className="acheck"><input type="checkbox" checked={devIds.includes(d.id)} onChange={() => tog(devIds, setDevIds, d.id)} />{d.name} <span className="sub">{orgName(d.org_id)}</span></label>)}
        </div>
        <small>Optional — trackers outside those companies.</small>
      </div>
      {!k && <p className="sub">The key is shown once after you create it. Limits: 60 calls a minute, 48 hours per call.</p>}
    </Drawer>
  );
}
